import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';
import type { FormControl, UntypedFormGroup } from '@angular/forms';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';
import { SpColorPickerComponent } from '@components/sp-color-picker/src/sp-color-picker.component';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { ThemeStateService } from '@sites/site-theme/src/state';

@Component({
    selector: 'sp-shadow-settings',
    templateUrl: './sp-shadow-settings.component.html',
    styleUrls: ['./sp-shadow-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, ReactiveFormsModule, SpColorPickerComponent, SpInputNumComponent],
})
export class SpShadowSettingsComponent implements OnInit, OnChanges {
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly themeState = inject(ThemeStateService);

    @Input() public shadowSettings: ShadowSettings;
    @Output() public valueChangeEvent = new EventEmitter<ShadowSettings>();

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public form: UntypedFormGroup;
    private readonly unsubscribe$ = new Subject<void>();

    constructor() {
        this.initForm();
    }

    public ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((changes) => this.emitChanges(changes));
        this.setFormData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { shadowSettings } = changes;

        if (shadowSettings?.currentValue && !shadowSettings.isFirstChange()) {
            this.form.patchValue(shadowSettings.currentValue, { emitEvent: false });
        }
    }

    private initForm(): void {
        this.form = this.fb.group({
            offsetX: DEFAULT_SHADOW_SETTINGS.offsetX,
            offsetY: DEFAULT_SHADOW_SETTINGS.offsetY,
            blur: DEFAULT_SHADOW_SETTINGS.blur,
            spread: DEFAULT_SHADOW_SETTINGS.spread,
            color: DEFAULT_SHADOW_SETTINGS.color,
        });
    }

    private setFormData(): void {
        if (this.shadowSettings) {
            this.form.patchValue(this.shadowSettings, { emitEvent: false });
        }
    }

    private emitChanges(changes: ShadowSettings): void {
        this.valueChangeEvent.emit(changes);
    }

    public setShadowColor({ value }: ColorPickerEventPayload): void {
        if (this.hasExplicitAlpha(value)) {
            this.color.setValue(value);
        } else {
            const currentAlpha = this.colorAlphaValue;
            this.color.setValue(currentAlpha < 100 ? this.applyAlphaToColor(value, currentAlpha) : value);
        }
    }

    public setColorAlpha(alpha: number): void {
        this.color.setValue(this.applyAlphaToColor(this.colorValue, alpha));
    }

    public get colorAlphaValue(): number {
        return this.extractAlpha(this.colorValue);
    }

    private hasExplicitAlpha(color: string): boolean {
        return /^rgba\(/.test(color) || (color.startsWith('#') && color.length === 9);
    }

    private extractAlpha(color: string): number {
        const rgbaMatch = color.match(/rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+))?\s*\)/);

        if (rgbaMatch) {
            const alpha = rgbaMatch[1] !== undefined ? parseFloat(rgbaMatch[1]) : 1;

            return Math.round(alpha * 100);
        }

        if (color.startsWith('#') && color.length === 9) {
            return Math.round((parseInt(color.slice(7, 9), 16) / 255) * 100);
        }

        return 100;
    }

    private applyAlphaToColor(color: string, alpha: number): string {
        const a = alpha / 100;
        const rgbaMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+)?\s*\)/);

        if (rgbaMatch) {
            return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${a})`;
        }

        const hex = color.replace('#', '');
        let r: number, g: number, b: number;

        if (hex.length === 3 || hex.length === 4) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }

        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    public setOffsetX(value: number): void {
        this.offsetX.setValue(value);
    }

    public setOffsetY(value: number): void {
        this.offsetY.setValue(value);
    }

    public setBlur(value: number): void {
        this.blur.setValue(value);
    }

    public setSpread(value: number): void {
        this.spread.setValue(value);
    }

    public get offsetX(): FormControl<number> {
        return this.form.get('offsetX') as FormControl<number>;
    }

    public get offsetY(): FormControl<number> {
        return this.form.get('offsetY') as FormControl<number>;
    }

    public get blur(): FormControl<number> {
        return this.form.get('blur') as FormControl<number>;
    }

    public get spread(): FormControl<number> {
        return this.form.get('spread') as FormControl<number>;
    }

    public get color(): FormControl<string> {
        return this.form.get('color') as FormControl<string>;
    }

    public get colorValue(): string {
        return this.color.value;
    }

    public get offsetXValue(): number {
        return this.offsetX.value;
    }

    public get offsetYValue(): number {
        return this.offsetY.value;
    }

    public get blurValue(): number {
        return this.blur.value;
    }

    public get spreadValue(): number {
        return this.spread.value;
    }
}
